import React, { useEffect, useRef, useState } from 'react';
import cc from 'classnames';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AxiosStatus } from 'api/axiosConfig';
import api from 'api/api';
import { IApiResponse } from 'utils/models';
import LoaderPage from 'ui/LoaderPage/LoaderPage';
import ErrorPage from 'ui/ErrorPage/ErrorPage';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import TablePagination from 'ui/TableInputs/TablePagination/TablePagination';
import LoaderForTable from 'ui/LoaderForTable/LoaderForTable';
import { amountFormatter } from 'utils/formatters';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import NavigateBackBtn from 'ui/NavigateBackBtn/NavigateBackBtn';
import { IApiPendingBulkPrepaidTransfer, IPendingPrepaidCardLoad } from '../../PendingPrepaidCard.model';

interface IPendingBulkDetails {
  isOpen: boolean;
  data: IPendingPrepaidCardLoad;
  onCloseTable: () => void;
}

const PendingBulkPrepaidDetails = ({ isOpen, data, onCloseTable }: IPendingBulkDetails) => {
  const [loadingStatus, setLoadingStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const pendingLoading = loadingStatus === AxiosStatus.PENDING;
  const successLoaded = loadingStatus === AxiosStatus.SUCCESS;
  const failureLoaded = loadingStatus === AxiosStatus.FAILURE;
  const gridRef = useRef<AgGridReact<IPendingPrepaidCardLoad>>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [rowData, setRowData] = useState<IPendingPrepaidCardLoad[]>([]);
  // Requesting Data for Table
  const requestDataForTable = () => {
    gridRef.current?.api?.showLoadingOverlay();
    const requestBody = {
      bulkId: data.bulkId,
      pageSize,
      pageNumber,
    };
    api
      .post('transfer/transactionDrillDownByBulkId', requestBody)
      .then((datas: IApiPendingBulkPrepaidTransfer) => {
        const formattedData = JSON.parse(datas.transaction);
        setRowData(formattedData);
        setTotalRows(formattedData.length);
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        setLoadingStatus(AxiosStatus.FAILURE);
        gridRef.current?.api.hideOverlay();
      });
  };
  useEffect(requestDataForTable, [data.bulkId, pageNumber, pageSize]);

  // TODO fix double request for first render to display NoData component
  useEffect(() => {
    setLoadingStatus(AxiosStatus.PENDING);
    const requestBody = {
      bulkId: data.bulkId,
      pageSize,
      pageNumber,
    };
    api
      .post('transfer/transactionDrillDownByBulkId', requestBody)
      .then((datas: IApiPendingBulkPrepaidTransfer) => {
        const formattedData = JSON.parse(datas.transaction);
        setRowData(formattedData);
        setTotalRows(formattedData.length);
        setLoadingStatus(AxiosStatus.SUCCESS);
      })
      .catch((error: IApiResponse) => {
        setLoadingStatus(AxiosStatus.FAILURE);
        console.error(error);
      });
  }, [data.bulkId, pageNumber, pageSize]);

  const renderAmount = (prop: ICellRendererParams<IPendingPrepaidCardLoad>) => {
    if (prop.data && prop.data?.amount) {
      return <div>{amountFormatter('ghs', false)(String(prop.data?.amount))}</div>;
    }
    return null;
  };

  const renderNarration = (prop: ICellRendererParams<IPendingPrepaidCardLoad>) => {
    if (prop.data) {
      return <div>{prop.data?.narration}</div>;
    }
    return null;
  };

  const renderCustomerId = (prop: ICellRendererParams<IPendingPrepaidCardLoad>) => {
    if (prop.data && prop.data?.customerId) {
      return <div>{prop.data?.customerId}</div>;
    }
    return null;
  };
  const renderMobileNumber = (prop: ICellRendererParams<IPendingPrepaidCardLoad>) => {
    if (prop.data && prop.data?.phoneNumber) {
      return <div>{prop.data?.phoneNumber}</div>;
    }
    return null;
  };

  const renderCardNumber = (prop: ICellRendererParams<IPendingPrepaidCardLoad>) => {
    if (prop.data && prop.data?.cardLastDigit) {
      return <div>{`****${String(prop.data?.cardLastDigit)}`}</div>;
    }
    return null;
  };

  const renderCardName = (prop: ICellRendererParams<IPendingPrepaidCardLoad>) => {
    if (prop.data && prop.data?.cardName) {
      return <div>{prop.data.cardName}</div>;
    }
    return null;
  };

  const renderStatus = (prop: ICellRendererParams<IPendingPrepaidCardLoad>) => {
    if (prop.data) {
      return <div>{prop.data?.transactionStatus ? '' : 'Pending' }</div>;
    }
    return null;
  };
  const columnDefs: ColDef[] = [
    {
      field: 'customerId',
      headerName: 'Customer ID',
      flex: 1,
      minWidth: 150,
      cellRenderer: renderCustomerId,
    },
    {
      field: 'cardNumber',
      headerName: 'Card Number',
      flex: 1,
      minWidth: 150,
      cellRenderer: renderCardNumber,
    },
    {
      field: 'cardName',
      headerName: 'Card Name',
      flex: 1,
      minWidth: 160,
      cellRenderer: renderCardName,
    },
    { field: 'mobileNumber', headerName: 'Mobile Number', flex: 1, minWidth: 150, cellRenderer: renderMobileNumber },
    { field: 'amount', headerName: 'Amount', flex: 1, minWidth: 130, cellRenderer: renderAmount },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 150, cellRenderer: renderNarration },
    { field: 'status', headerName: 'Status', width: 220, cellRenderer: renderStatus },
  ];

  // Pagination & Filters handlers
  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPageNumber(newPage);
    requestDataForTable();
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newPageSize = parseInt(event.target.value as string, 10);
    setPageSize(newPageSize);
    setPageNumber(1);
    requestDataForTable();
  };

  return (
    <>
      {pendingLoading && <LoaderPage />}
      {failureLoaded && <ErrorPage />}
      {successLoaded && isOpen && (
        <>
          {/* <pre>{JSON.stringify(rowData, null, 2)}</pre> */}
          <DashboardCard>
            <NavigateBackBtn onBackBtnClick={onCloseTable}>Back</NavigateBackBtn>
            <NavigationTitle to="#/" layoutType={LayoutType.dashboard} onClick={onCloseTable}>
              Bulk Transfers List
            </NavigationTitle>
            <div className={cc('tableWrapper', 'ag-theme-material')}>
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                loadingOverlayComponent={LoaderForTable}
                columnDefs={columnDefs}
                defaultColDef={{ resizable: true }}
                suppressCellFocus
                enableCellTextSelection
                headerHeight={60}
                rowHeight={70}
                undoRedoCellEditing
                suppressRowHoverHighlight
                pagination
                paginationPageSize={pageSize}
                suppressPaginationPanel
                suppressScrollOnNewData
                domLayout="autoHeight"
              />
            </div>
            <TablePagination
              count={Math.ceil(totalRows / pageSize)}
              page={pageNumber}
              pageSize={pageSize}
              totalCount={totalRows}
              handlePageChange={handlePageChange}
              handlePageSizeChange={handlePageSizeChange}
            />
          </DashboardCard>
        </>
      )}
    </>
  );
};

export default PendingBulkPrepaidDetails;
